/**
 * Engine: the main-thread facade the UI talks to. It owns the AudioContext graph and wires the
 * AudioWorklet, analysis worker and renderer together with direct MessageChannels, so audio data
 * never passes through the main thread (ADR-0004).
 *
 *   source ─► inputGain ─┬─► AudioWorklet (features) ─frames─► renderer
 *                        │                └─onsets─► analysis worker ─transport+mood─► renderer
 *                        └─► DelayNode (latency compensation) ─► speakers   (app-monitored only)
 */
import featureWorkletUrl from './audio/feature.worklet.ts?worker&url';
import { HOP_SIZE } from '../shared/audioFrame';
import {
  FEATURE_PROCESSOR_NAME,
  type BeatGrid,
  type FromAnalysis,
  type FromRender,
  type OutputConfig,
  type RenderInitMessage,
  type RenderStats,
  type ToAnalysis,
  type ToRender,
  type WorkletConnectMessage,
} from '../shared/messages';
import { idleMood, type MoodState } from '../shared/mood';
import type { Scene, TransitionSpec } from '../shared/scene';
import type { Settings } from '../shared/settings';
import {
  DEFAULT_LATENCY,
  analysisTimeFor,
  compensationDelayFor,
  type LatencyInfo,
  type Monitoring,
} from '../shared/timing';
import type { TransportState } from '../shared/transport';
import type { VisualizationDef } from '../shared/viz';
import { DemoSource } from './audio/sources/DemoSource';
import {
  FileSource,
  MicSource,
  TabSource,
  listAudioDevices,
  type AudioDevices,
} from './audio/sources/inputs';
import { SourceError, type AudioSource, type SourceKind } from './audio/sources/types';
import { Renderer, WebGLUnavailableError } from './render/Renderer';
import { KIND_INSTALLERS } from './render/viz/kinds';

export type { SourceKind } from './audio/sources/types';
export type { AudioDevices } from './audio/sources/inputs';

export interface SourceInfo {
  kind: SourceKind;
  label: string;
  monitoring: Monitoring;
  playing: boolean;
  canPause: boolean;
  canSeek: boolean;
}

export interface GridStatus {
  state: 'analyzing' | 'ready' | 'failed';
  progress: number;
  grid?: BeatGrid;
}

export interface EngineEvents {
  stats(stats: RenderStats): void;
  transport(t: TransportState, mood: MoodState): void;
  source(info: SourceInfo | null): void;
  renderer(info: { name: string; mode: 'worker' | 'main' }): void;
  error(message: string): void;
  /** File playback position, ~4 Hz. */
  media?(pos: { time: number; duration: number }): void;
  grid?(status: GridStatus | null): void;
  ended?(): void;
  milkdrop?(names: string[]): void;
  slowLayer?(layerId: string, name: string): void;
  /** A live frame for the recorder (owner must close it). */
  liveFrame?(bitmap: ImageBitmap): void;
}

export interface CanvasProvider {
  canvas(): HTMLCanvasElement;
  /** Size of the rendered frame (CSS px) and device pixel ratio. */
  size(): { width: number; height: number; dpr: number };
}

/** Persistent cache for file beat grids (keyed by content hash). */
export interface GridCache {
  get(id: string): Promise<BeatGrid | undefined>;
  set(id: string, grid: BeatGrid): Promise<void>;
}

export interface StartOptions {
  file?: File;
  deviceId?: string;
}

const PROBE_TIMEOUT_MS = 3000;
const LATENCY_POLL_MS = 2000;
const MEDIA_POLL_MS = 250;
const MAX_PREANALYSIS_SECONDS = 20 * 60;

export class Engine {
  private ctx: AudioContext | null = null;
  private booting: Promise<void> | null = null;
  private inputGain!: GainNode;
  private delay!: DelayNode;
  private recordTap: MediaStreamAudioDestinationNode | null = null;
  private worklet!: AudioWorkletNode;
  private analysis: Worker | null = null;
  private renderWorker: Worker | null = null;
  private mainRenderer: Renderer | null = null;
  private source: AudioSource | null = null;
  private latency: LatencyInfo = { ...DEFAULT_LATENCY };
  private timers: ReturnType<typeof setInterval>[] = [];
  private disposed = false;
  private customViz: VisualizationDef[] = [];
  private thumbnailId = 0;
  private readonly thumbnails = new Map<
    number,
    (img: { width: number; height: number; pixels: Uint8Array }) => void
  >();
  private preanalysis: Worker | null = null;
  private lastTransport: TransportState | null = null;
  private lastMood: MoodState = idleMood();

  constructor(
    private readonly canvases: CanvasProvider,
    private readonly events: EngineEvents,
    private scene: Scene,
    private settings: Settings,
    private readonly gridCache?: GridCache,
  ) {}

  get running(): boolean {
    return this.source !== null;
  }

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  /**
   * Boot the analysis and renderer before there's any sound, so the visuals show from the first
   * frame. Needs no user gesture: the AudioContext stays suspended until `start` resumes it.
   */
  async prepare(): Promise<void> {
    this.ctx ??= new AudioContext({ latencyHint: 'interactive' });
    await (this.booting ??= this.boot(this.ctx));
  }

  /**
   * Start (or switch to) a source. Call it directly from a user gesture: the AudioContext and the
   * permission prompts (mic, tab capture) need the gesture's activation.
   */
  async start(kind: SourceKind, opts: StartOptions = {}): Promise<void> {
    if (!this.ctx) this.ctx = new AudioContext({ latencyHint: 'interactive' });
    const ctx = this.ctx;
    const resumed = ctx.resume();
    const opened = this.openSource(ctx, kind, opts); // runs synchronously up to its first await
    try {
      await resumed;
      await (this.booting ??= this.boot(ctx));
      const src = await opened;
      await this.attach(src);
    } catch (err) {
      const message =
        err instanceof SourceError ? err.userMessage : `Could not start audio: ${String(err)}`;
      this.events.error(message);
      throw err;
    }
  }

  togglePlay(): void {
    this.source?.togglePlay?.();
    this.emitSource();
    this.postMediaClock();
  }

  seek(seconds: number): void {
    if (this.source instanceof FileSource) {
      this.source.seek(seconds);
      setTimeout(() => this.postMediaClock(), 50);
    }
  }

  // ---- beat controls

  /** Analysis time of what the listener is hearing right now. */
  private heardNow(): number {
    return analysisTimeFor(this.graphNow(), this.latency, this.settings.syncOffsetMs / 1000);
  }

  tap(): void {
    if (this.ctx) this.postAnalysis({ type: 'tap', time: this.heardNow() });
  }

  nudge(direction: -1 | 1): void {
    this.postAnalysis({ type: 'nudge', seconds: direction * 0.01 });
  }

  scaleTempo(factor: 0.5 | 2): void {
    this.postAnalysis({ type: 'scaleTempo', factor });
  }

  resync(): void {
    if (this.ctx) this.postAnalysis({ type: 'resync', time: this.heardNow() });
  }

  /** A MIDI clock beat that happened at `perfMs` (performance.now() time base). */
  externalBeat(perfMs: number, bpm: number): void {
    if (!this.ctx) return;
    const t = this.heardNow() - (performance.now() - perfMs) / 1000;
    this.postAnalysis({ type: 'externalBeat', time: t, bpm });
  }

  externalStop(): void {
    this.postAnalysis({ type: 'externalStop' });
  }

  // ---- scene, settings, master

  setScene(scene: Scene, transition?: TransitionSpec): void {
    this.scene = scene;
    this.postRender(transition ? { type: 'scene', scene, transition } : { type: 'scene', scene });
  }

  /** Preview mode: the scene being prepared (shown in the main window); null leaves it. */
  setPreview(scene: Scene | null): void {
    this.postRender({ type: 'preview', scene });
  }

  /** Recording: receive live frames of this size through events.liveFrame (0×0 stops). */
  recordLive(width: number, height: number): void {
    this.postRender({ type: 'recordLive', width, height });
  }

  /** Preview-mode takes (start, hold on the T-bar, resume, complete, cancel, cut). */
  take(msg: Extract<ToRender, { type: `take.${string}` }>): void {
    this.postRender(msg);
  }

  setSettings(settings: Settings): void {
    const prev = this.settings;
    this.settings = settings;
    this.postRender({ type: 'settings', settings });
    if (
      prev.bpmMin !== settings.bpmMin ||
      prev.bpmMax !== settings.bpmMax ||
      prev.phraseBars !== settings.phraseBars ||
      prev.beatDetection !== settings.beatDetection ||
      prev.beatsPerBar !== settings.beatsPerBar
    ) {
      this.postAnalysis(analysisConfig(settings));
    }
    if (this.ctx && this.inputGain)
      this.inputGain.gain.setTargetAtTime(settings.inputGain, this.ctx.currentTime, 0.02);
    if (prev.monitorInput !== settings.monitorInput) this.routeMonitoring();
    this.updateLatency();
  }

  setCustomViz(defs: VisualizationDef[]): void {
    this.customViz = defs;
    this.postRender({ type: 'customViz', defs });
  }

  setMaster(state: { blackout: boolean; freeze: boolean }): void {
    this.postRender({ type: 'master', ...state });
  }

  /** Hand the integrity-checked MilkDrop preset pack to the renderer. */
  loadMilkdropPack(code: string): void {
    this.postRender({ type: 'milkdropPack', code });
  }

  midiValue(source: string, value: number): void {
    this.postRender({ type: 'midi', source, value });
  }

  /** Capture a small thumbnail of the current frame. */
  thumbnail(
    width: number,
    height: number,
  ): Promise<{ width: number; height: number; pixels: Uint8Array }> {
    const id = ++this.thumbnailId;
    return new Promise((resolve, reject) => {
      if (!this.renderWorker && !this.mainRenderer)
        return reject(new Error('renderer not running'));
      const timer = setTimeout(() => {
        this.thumbnails.delete(id);
        reject(new Error('thumbnail timed out'));
      }, 2000);
      this.thumbnails.set(id, (img) => {
        clearTimeout(timer);
        resolve(img);
      });
      this.postRender({ type: 'thumbnail', id, width, height });
    });
  }

  resize(): void {
    const { width, height, dpr } = this.canvases.size();
    this.postRender({ type: 'resize', width, height, dpr });
  }

  // ---- outputs

  addOutput(
    id: string,
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    config: OutputConfig,
  ): void {
    if (this.renderWorker) {
      const offscreen = canvas.transferControlToOffscreen();
      this.renderWorker.postMessage(
        { type: 'addOutput', id, canvas: offscreen, width, height, config } satisfies ToRender,
        [offscreen],
      );
    } else {
      this.mainRenderer?.handle({ type: 'addOutput', id, canvas, width, height, config });
    }
  }

  updateOutput(
    id: string,
    patch: { width?: number; height?: number; config?: Partial<OutputConfig> },
  ): void {
    this.postRender({ type: 'updateOutput', id, ...patch });
  }

  removeOutput(id: string): void {
    this.postRender({ type: 'removeOutput', id });
  }

  // ---- devices

  async devices(): Promise<AudioDevices> {
    return listAudioDevices();
  }

  async setOutputDevice(deviceId: string): Promise<boolean> {
    const ctx = this.ctx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!ctx?.setSinkId) return false;
    try {
      await ctx.setSinkId(deviceId);
      this.updateLatency();
      return true;
    } catch {
      return false;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.source?.stop();
    this.source = null;
    for (const t of this.timers) clearInterval(t);
    this.renderWorker?.terminate();
    this.analysis?.terminate();
    this.preanalysis?.terminate();
    this.mainRenderer?.dispose();
    void this.ctx?.close();
  }

  // ---- boot

  private async boot(ctx: AudioContext): Promise<void> {
    await ctx.audioWorklet.addModule(featureWorkletUrl);
    this.worklet = new AudioWorkletNode(ctx, FEATURE_PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 2,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    // Keep the worklet pulled by the graph without making a sound.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    this.worklet.connect(mute).connect(ctx.destination);

    this.inputGain = ctx.createGain();
    this.inputGain.gain.value = this.settings.inputGain;
    this.inputGain.connect(this.worklet);
    this.delay = ctx.createDelay(1);
    this.delay.connect(ctx.destination);

    const frames = new MessageChannel();
    const onsets = new MessageChannel();
    const transport = new MessageChannel();
    const connect: WorkletConnectMessage = {
      type: 'connect',
      framePort: frames.port1,
      onsetPort: onsets.port1,
    };
    this.worklet.port.postMessage(connect, [frames.port1, onsets.port1]);

    this.analysis = new Worker(new URL('./analysis/analysis.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.analysis.onmessage = (e: MessageEvent<FromAnalysis>) => {
      if (e.data.type === 'transport') {
        this.lastTransport = e.data.transport;
        this.lastMood = e.data.mood;
        this.events.transport(e.data.transport, e.data.mood);
      }
    };
    this.postAnalysis(
      {
        type: 'init',
        onsetPort: onsets.port2,
        transportPort: transport.port1,
        hopRate: ctx.sampleRate / HOP_SIZE,
      },
      [onsets.port2, transport.port1],
    );
    this.postAnalysis(analysisConfig(this.settings));

    await this.startRenderer(frames.port2, transport.port2);

    this.timers.push(setInterval(() => this.updateLatency(), LATENCY_POLL_MS));
    this.timers.push(setInterval(() => this.pollMedia(), MEDIA_POLL_MS));
    document.addEventListener('visibilitychange', () =>
      this.postRender({ type: 'visibility', visible: document.visibilityState === 'visible' }),
    );
  }

  private async startRenderer(framePort: MessagePort, transportPort: MessagePort): Promise<void> {
    const canvas = this.canvases.canvas();
    const { width, height, dpr } = this.canvases.size();
    const base: Omit<RenderInitMessage, 'canvas'> = {
      type: 'init',
      width,
      height,
      dpr,
      framePort,
      transportPort,
      scene: this.scene,
      settings: this.settings,
      latency: this.latency,
      customViz: this.customViz,
    };

    if (!this.settings.forceMainThreadRender) {
      const worker = new Worker(new URL('./render/render.worker.ts', import.meta.url), {
        type: 'module',
      });
      const workerOk = 'transferControlToOffscreen' in canvas && (await probeWorker(worker));
      if (workerOk) {
        this.renderWorker = worker;
        worker.onmessage = (e: MessageEvent<FromRender>) => this.onRender(e.data, 'worker');
        const offscreen = canvas.transferControlToOffscreen();
        const init: RenderInitMessage = { ...base, canvas: offscreen };
        worker.postMessage(init, [offscreen, framePort, transportPort]);
        return;
      }
      worker.terminate();
    }
    try {
      this.mainRenderer = new Renderer(
        canvas,
        base,
        { post: (m) => this.onRender(m, 'main'), requestFrame: (cb) => requestAnimationFrame(cb) },
        'main',
        KIND_INSTALLERS,
      );
    } catch (err) {
      this.events.error(
        err instanceof WebGLUnavailableError
          ? 'WebGL 2 is unavailable. Check that hardware acceleration is enabled in your browser settings.'
          : `Renderer failed to start: ${String(err)}`,
      );
    }
  }

  private onRender(msg: FromRender, mode: 'worker' | 'main'): void {
    switch (msg.type) {
      case 'liveFrame':
        if (this.events.liveFrame) this.events.liveFrame(msg.bitmap);
        else msg.bitmap.close();
        break;
      case 'ready':
        this.events.renderer({ name: msg.renderer, mode });
        break;
      case 'stats':
        this.events.stats(msg.stats);
        break;
      case 'thumbnail':
        this.thumbnails.get(msg.id)?.(msg);
        this.thumbnails.delete(msg.id);
        break;
      case 'milkdropPresets':
        this.events.milkdrop?.(msg.names);
        break;
      case 'slowLayer':
        this.events.slowLayer?.(msg.layerId, msg.name);
        break;
      case 'error':
        this.events.error(
          msg.code === 'webgl2-unavailable'
            ? 'WebGL 2 is unavailable. Check that hardware acceleration is enabled in your browser settings.'
            : msg.message,
        );
        break;
    }
  }

  // ---- sources

  private openSource(
    ctx: AudioContext,
    kind: SourceKind,
    opts: StartOptions,
  ): Promise<AudioSource> {
    switch (kind) {
      case 'demo':
        return Promise.resolve(new DemoSource(ctx));
      case 'file':
        if (!opts.file) return Promise.reject(new SourceError('no file', 'No file was selected.'));
        return Promise.resolve(new FileSource(ctx, opts.file));
      case 'mic':
        return MicSource.open(ctx, opts.deviceId);
      case 'tab':
        return TabSource.open(ctx);
    }
  }

  private async attach(src: AudioSource): Promise<void> {
    if (this.disposed) {
      src.stop();
      return;
    }
    const prev = this.source;
    if (prev) {
      prev.onEnded = null;
      prev.stop();
      prev.node.disconnect();
    }
    this.source = src;
    src.node.connect(this.inputGain);
    this.routeMonitoring();
    src.onEnded = () => {
      this.emitSource();
      this.events.ended?.();
    };
    this.postAnalysis({ type: 'grid', grid: null });
    this.events.grid?.(null);
    await src.start();
    this.postAnalysis({ type: 'reset' });
    this.updateLatency();
    this.emitSource();
    if (src instanceof FileSource) void this.analyzeFile(src);
  }

  /**
   * Audio for a recording. When the app plays the source, the delayed monitor path is what lines
   * up with the pictures; otherwise (mic, tab capture) the input itself.
   */
  recordingAudio(): MediaStreamTrack | null {
    if (!this.ctx || !this.source) return null;
    this.stopRecordingAudio();
    this.recordTap ??= this.ctx.createMediaStreamDestination();
    (this.source.monitoring === 'app' ? this.delay : this.inputGain).connect(this.recordTap);
    return this.recordTap.stream.getAudioTracks()[0] ?? null;
  }

  stopRecordingAudio(): void {
    if (!this.recordTap) return;
    for (const node of [this.delay, this.inputGain]) {
      try {
        node.disconnect(this.recordTap);
      } catch {
        // wasn't connected
      }
    }
  }

  /** Play app-monitored sources (and, if enabled, the mic) through the delay to the speakers. */
  private routeMonitoring(): void {
    const src = this.source;
    if (!src || !this.delay) return;
    try {
      src.node.disconnect(this.delay);
    } catch {
      // wasn't connected
    }
    if (src.monitoring === 'app' || (src.kind === 'mic' && this.settings.monitorInput))
      src.node.connect(this.delay);
  }

  private emitSource(): void {
    const s = this.source;
    this.events.source(
      s
        ? {
            kind: s.kind,
            label: s.label,
            monitoring: s.monitoring,
            playing: s.playing,
            canPause: s.canPause,
            canSeek: s instanceof FileSource,
          }
        : null,
    );
  }

  private pollMedia(): void {
    const s = this.source;
    if (!(s instanceof FileSource)) return;
    this.events.media?.(s.position);
    this.postMediaClock();
  }

  private postMediaClock(): void {
    const s = this.source;
    if (!(s instanceof FileSource) || !this.ctx) return;
    // The sample the graph is processing now ↔ the element's current position.
    this.postAnalysis({
      type: 'mediaClock',
      mediaTime: s.element.currentTime,
      graphTime: this.graphNow(),
      playing: s.playing,
    });
  }

  /** Decode the file and find its beat grid, downbeats and drops in a background worker. */
  private async analyzeFile(src: FileSource): Promise<void> {
    const file = src.file;
    try {
      const buf = await file.arrayBuffer();
      const hash = await digest(buf);
      const cached = await this.gridCache?.get(hash).catch(() => undefined);
      if (cached) return this.useGrid(src, cached);
      this.events.grid?.({ state: 'analyzing', progress: 0 });
      const ctx = this.ctx!;
      const audio = await ctx.decodeAudioData(buf.slice(0));
      if (this.source !== src) return;
      const n = Math.min(audio.length, Math.floor(MAX_PREANALYSIS_SECONDS * audio.sampleRate));
      const mono = new Float32Array(n);
      for (let c = 0; c < audio.numberOfChannels; c++) {
        const ch = audio.getChannelData(c);
        for (let i = 0; i < n; i++) mono[i]! += ch[i]! / audio.numberOfChannels;
      }
      this.preanalysis?.terminate();
      const worker = new Worker(new URL('./analysis/preanalysis.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.preanalysis = worker;
      const grid = await new Promise<BeatGrid>((resolve, reject) => {
        worker.onmessage = (
          e: MessageEvent<FromAnalysis | { type: 'progress'; progress: number }>,
        ) => {
          if (e.data.type === 'progress')
            this.events.grid?.({ state: 'analyzing', progress: e.data.progress });
          if (e.data.type === 'preanalyzed') resolve(e.data.grid);
        };
        worker.onerror = (e) => reject(new Error(e.message));
        worker.postMessage(
          {
            type: 'preanalyze',
            id: hash,
            sampleRate: audio.sampleRate,
            samples: mono,
          } satisfies ToAnalysis,
          [mono.buffer],
        );
      });
      worker.terminate();
      if (this.preanalysis === worker) this.preanalysis = null;
      await this.gridCache?.set(hash, grid).catch(() => undefined);
      if (this.source === src) this.useGrid(src, grid);
    } catch {
      if (this.source === src) this.events.grid?.({ state: 'failed', progress: 0 });
    }
  }

  private useGrid(src: FileSource, grid: BeatGrid): void {
    if (this.source !== src) return;
    this.postAnalysis({ type: 'grid', grid });
    this.postMediaClock();
    this.events.grid?.({ state: 'ready', progress: 1, grid });
  }

  // ---- latency

  private updateLatency(): void {
    const ctx = this.ctx;
    if (!ctx || !this.delay) return;
    const src = this.source;
    const monitoring = src?.monitoring ?? 'app';
    // outputLatency is missing in some browsers; assume a typical value there.
    const output =
      (ctx.baseLatency ?? 0) + (typeof ctx.outputLatency === 'number' ? ctx.outputLatency : 0.015);
    const comp =
      this.settings.delayCompensation && monitoring === 'app'
        ? compensationDelayFor(
            this.settings.visualLatencyMs / 1000,
            output,
            HOP_SIZE / ctx.sampleRate,
          )
        : 0;
    if (Math.abs(this.delay.delayTime.value - comp) > 0.002) {
      this.delay.delayTime.setTargetAtTime(comp, ctx.currentTime, 0.05);
    }
    this.latency = {
      monitoring,
      outputLatencyS: output,
      inputLatencyS: src?.inputLatencyS ?? 0.01,
      compensationDelayS: comp,
    };
    this.postRender({ type: 'latency', latency: this.latency });
  }

  /** Graph time "now": the time of the audio the graph is currently processing. */
  private graphNow(): number {
    const ctx = this.ctx!;
    const ts = ctx.getOutputTimestamp?.();
    if (
      ts?.contextTime !== undefined &&
      ts.performanceTime !== undefined &&
      ts.performanceTime > 0
    ) {
      return (
        ts.contextTime +
        (performance.now() - ts.performanceTime) / 1000 +
        this.latency.outputLatencyS
      );
    }
    return ctx.currentTime;
  }

  /** Most recent transport and mood (for Auto show and UI). */
  get transport(): { transport: TransportState | null; mood: MoodState } {
    return { transport: this.lastTransport, mood: this.lastMood };
  }

  // ---- messaging

  private postRender(msg: ToRender): void {
    if (this.renderWorker) this.renderWorker.postMessage(msg);
    else this.mainRenderer?.handle(msg);
  }

  private postAnalysis(msg: ToAnalysis, transfer: Transferable[] = []): void {
    this.analysis?.postMessage(msg, transfer);
  }
}

function probeWorker(worker: Worker): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    worker.onmessage = (e: MessageEvent<{ type: string; ok?: boolean }>) => {
      if (e.data.type === 'probe-result') {
        clearTimeout(timer);
        resolve(e.data.ok === true);
      }
    };
    worker.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    worker.postMessage({ type: 'probe' });
  });
}

async function digest(buf: ArrayBuffer): Promise<string> {
  const head = buf.byteLength > 4_000_000 ? buf.slice(0, 4_000_000) : buf;
  const hash = await crypto.subtle.digest('SHA-256', head);
  return `${buf.byteLength}-${[...new Uint8Array(hash)]
    .slice(0, 12)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')}`;
}

function analysisConfig(s: Settings): Extract<ToAnalysis, { type: 'config' }> {
  return {
    type: 'config',
    bpmMin: s.bpmMin,
    bpmMax: s.bpmMax,
    phraseBars: s.phraseBars,
    detect: s.beatDetection,
    beatsPerBar: s.beatsPerBar,
  };
}
